import { useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface CheckboxProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  className?: string;
  highlight?: boolean;
  /** Hint visuel pour la validation EN PRÉPARATION/DISPONIBLE (cf. lib/bienPrepValidation.ts).
   *  Pour les booleans, isFilled() retourne true dès que la valeur n'est pas null/undefined,
   *  donc le hint ne s'active que lorsque le champ est explicitement non renseigné en DB. */
  missingHint?: "required" | "mailing";
  /**
   * Case verrouillée : même taille, même place, mais inerte — ni survol, ni
   * clic, ni curseur main. Contrat identique à `FormField.readOnly`, pour que
   * le helper `F()` des fiches rende LE MÊME composant plutôt que d'en
   * substituer un autre de géométrie différente.
   */
  readOnly?: boolean;
}

export function Checkbox({
  checked = false,
  onChange,
  label,
  className,
  highlight: _highlight, // intentionnellement ignoré (cf. note ci-dessous)
  missingHint,
  readOnly = false,
}: CheckboxProps) {
  // Note : `highlight` accepté pour compatibilité d'API (les autres composants
  // de form l'utilisent), mais ignoré pour Checkbox — un boolean est toujours
  // une réponse explicite, donc colorer le label n'apporte rien.
  void _highlight;
  const [isHovered, setIsHovered] = useState(false);

  // Bordure colorée si le champ est vide ET un missingHint est actif.
  // Pour un Checkbox, "vide" = ni true ni false (mais le composant force checked à un boolean,
  // donc en pratique cette branche est rarement atteinte — elle existe pour rester cohérent
  // visuellement avec FormField/SelectField/DateField si jamais un parent passe explicitement
  // missingHint sans avoir initialisé la valeur).
  const showMissingBorder = !readOnly && !!missingHint;
  const missingBorderHex =
    missingHint === "required"
      ? "var(--color-danger)"
      : missingHint === "mailing"
        ? "var(--color-warning)"
        : null;

  const boxClasses = checked
    ? "bg-accent border border-solid border-transparent"
    : isHovered
      ? "bg-surface-2 border border-dashed"
      : "bg-surface border border-dashed";

  const boxStyle = !checked
    ? { borderColor: showMissingBorder && missingBorderHex
        ? missingBorderHex
        : (isHovered ? "var(--color-text-secondary)" : "var(--color-border)") }
    : undefined;

  // Un Checkbox a toujours une réponse explicite (true ou false), donc on ne
  // colore JAMAIS le label par missingHint — false est une réponse valide,
  // pas un manque. Idem pour highlight (c_level pré-existant) : sans contexte
  // de "champ vide", la coloration n'apporte rien. On garde uniquement la
  // logique cosmétique checked/hover/muted.
  const labelColorClass = checked
    ? "text-text-primary"
    : isHovered
      ? "text-text-secondary"
      : "text-text-muted";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 select-none",
        // `cn` est un simple clsx : on tranche le curseur ici plutôt que de
        // laisser deux classes `cursor-*` cohabiter.
        readOnly ? "cursor-default" : "cursor-pointer",
        className
      )}
      onMouseEnter={() => !readOnly && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => !readOnly && onChange?.(!checked)}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        style={boxStyle}
        className={cn(
          "w-4 h-4 rounded-sm flex items-center justify-center transition-colors duration-150",
          "outline-none shadow-none",
          boxClasses
        )}
      >
        {checked && <Check size={12} className="text-white" strokeWidth={3} />}
      </button>
      {label && (
        <span className={cn(
          "text-sm transition-colors duration-150",
          labelColorClass
        )}>
          {label}
        </span>
      )}
    </div>
  );
}
